import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/api';
import { AxiosError } from 'axios';

// Define types
interface User {
  // Define based on user data you expect from backend (e.g., after fetching /users/me/)
  id: number;
  email: string;
  // Add other fields like name, etc.
}

// Define the shape of the context data
export interface AuthContextType {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, pass: string) => Promise<void>;
  logout: () => void;
  register: (email: string, pass: string, pass2: string) => Promise<void>;
  // Add fetchUser if needed separately
}

// Create the context with a default value
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Create Provider Component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }: AuthProviderProps) => {
  const [accessToken, setAccessToken] = useState<string | null>(localStorage.getItem('access_token'));
  const [refreshToken, setRefreshToken] = useState<string | null>(localStorage.getItem('refresh_token'));
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Start loading until initial check is done
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Effect to check token validity and fetch user on initial load
  useEffect(() => {
    const initializeAuth = async () => {
      if (accessToken) {
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        // Optionally: Fetch user data here if token exists
        // try {
        //   const { data } = await apiClient.get('/users/me/'); // Adjust endpoint if needed
        //   setUser(data);
        // } catch (e) {
        //   console.error('Failed to fetch user on init', e);
        //   // Token might be invalid, clear state
        //   setAccessToken(null);
        //   setRefreshToken(null);
        //   localStorage.removeItem('access_token');
        //   localStorage.removeItem('refresh_token');
        //   apiClient.defaults.headers.common['Authorization'] = '';
        // }
      } else {
         apiClient.defaults.headers.common['Authorization'] = '';
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, [accessToken]); // Rerun if accessToken changes externally (e.g., after refresh)

  const handleApiError = (err: unknown, context: 'login' | 'register' | 'other' = 'other'): string => {
    let errorMessage = 'An unexpected error occurred.';
      if (err instanceof AxiosError) {
        if (err.response) {
          // Specific handling for 401 during login
          if (err.response.status === 401 && context === 'login') {
             errorMessage = 'Invalid email or password. Please try again.';
          } else if (err.response.status === 401) {
             // Generic 401 message for other contexts
             errorMessage = err.response.data.detail || 'Authentication failed.';
          } else if (err.response.data && typeof err.response.data === 'object') {
             errorMessage = Object.entries(err.response.data)
              .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
              .join('; ') || errorMessage;
          } else {
             errorMessage = `Request failed (${err.response.status})`;
          }
        } else if (err.request) {
          errorMessage = 'No response from server.';
        } else {
          errorMessage = err.message;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      return errorMessage;
  }

  // Login Function
  const login = async (email: string, pass: string) => {
    setIsLoading(true);
    setError(null);
    try {
      // Send 'username' field with the email value, as expected by default SimpleJWT view
      const response = await apiClient.post('/token/', { username: email, password: pass });
      const { access, refresh } = response.data;
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      setAccessToken(access);
      setRefreshToken(refresh);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${access}`;
      // Optionally fetch user data after successful login
      // const { data: userData } = await apiClient.get('/users/me/');
      // setUser(userData);
      navigate('/dashboard'); // Navigate to dashboard after login
    } catch (err) {
      console.error('Login failed in context:', err);
      const errorMessage = handleApiError(err, 'login'); // Pass context as 'login'
      setError(errorMessage);
      // Ensure tokens/auth header are cleared on login failure
      logout(); // Call logout to clear state
      throw new Error(errorMessage); // Re-throw error for form handling
    } finally {
      setIsLoading(false);
    }
  };

  // Register Function
  const register = async (email: string, pass: string, pass2: string) => {
     setIsLoading(true);
    setError(null);
    try {
      // Assuming registration endpoint requires email and password
      await apiClient.post('/auth/register/', { email, password: pass, password2: pass2 });
      // Optionally: Navigate to login page or show success message
      navigate('/login'); // Navigate to login page after successful registration
      alert('Registration successful! Please log in.'); // Or use a more sophisticated notification
    } catch (err) {
      console.error('Registration failed in context:', err);
      const errorMessage = handleApiError(err, 'register'); // Pass context as 'register'
      setError(errorMessage);
      throw new Error(errorMessage); // Re-throw error for form handling
    } finally {
      setIsLoading(false);
    }
  };

  // Logout Function
  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    setError(null);
    delete apiClient.defaults.headers.common['Authorization'];
    navigate('/login'); // Redirect to login on logout
  };

  // Context Value
  const value = {
    accessToken,
    refreshToken,
    user,
    isAuthenticated: !!accessToken,
    isLoading,
    error,
    login,
    logout,
    register,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom Hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 